// app/src/lib/cashflow/rent.js — detect rent + NNN payments from bank activity.
//
// Restaurants typically pay one base rent that's capped by the lease, plus a
// triple-net (NNN) share of property taxes, insurance, CAM, and utilities
// that the landlord can raise year over year. Operators want to watch the
// year-over-year drift on the total, because the NNN reconciliation often
// outpaces the lease cap they assumed they were protected by.
//
// Detection: same user-driven model as taxes — no built-in defaults. The
// owner enters the descriptors that show up on their rent/NNN payments
// (typically the landlord/property management name plus any utility
// descriptors), and Kanjo classifies any matching outflow as rent. Storage
// lives on userProfile.rentKeywords.

import { db } from '../db.js';
import { currentClaims } from '../auth/cognitoClient.js';
import { categorizeExpense } from './expenseKeywords.js';

const MAX_KEYWORDS = 25;

export async function getKeywords() {
  const c = await currentClaims();
  if (!c) return [];
  const p = await db.userProfile.get(c.sub);
  return (p && Array.isArray(p.rentKeywords)) ? p.rentKeywords : [];
}

export async function setKeywords(list) {
  const c = await currentClaims();
  if (!c) throw new Error('rent: not signed in');
  const clean = [...new Set((list || []).map((s) => String(s).trim()).filter(Boolean))].slice(0, MAX_KEYWORDS);
  const existing = (await db.userProfile.get(c.sub)) || { userId: c.sub };
  await db.userProfile.put({ ...existing, userId: c.sub, rentKeywords: clean });
  return clean;
}

// Same leading-word-boundary matcher used by taxes — a bare token only hits
// at the start of a word, so descriptors like "ABC RE" don't catch random
// substrings inside ACH trace numbers or memo strings.
function escapeRegex(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
function matchesKeyword(hay, k) {
  const key = String(k || '').toLowerCase();
  if (!key) return false;
  if (/[^\w ]/.test(key)) return hay.includes(key);
  return new RegExp('\\b' + escapeRegex(key), 'i').test(hay);
}

export function isRent(tx, keywords) {
  if (!tx || tx.pending) return false;
  if ((tx.amountCents || 0) <= 0) return false; // outflows only
  if (!keywords || keywords.length === 0) return false;
  const hay = ((tx.merchantName || '') + ' ' + (tx.description || '')).toLowerCase();
  return keywords.some((k) => matchesKeyword(hay, k));
}

/** A posted utility outflow (electric/gas/internet per the operator's expense
 *  keywords, e.g. Puget Sound Energy). Rent takes precedence — callers check
 *  isRent first so an NNN payment naming utilities still counts once, as rent. */
export function isUtilityBill(tx, expenseKeywords) {
  if (!tx || tx.pending) return false;
  if ((tx.amountCents || 0) <= 0) return false; // outflows only
  if (!expenseKeywords) return false;
  return categorizeExpense(tx.merchantName, tx.description, expenseKeywords) === 'utility';
}

/** A posted MRO outflow (maintenance, repair & operations — grease trap, hood
 *  cleaning, HVAC, plumbing … per the operator's maintenance keywords). Rent
 *  takes precedence, same as utilities. */
export function isMroBill(tx, expenseKeywords) {
  if (!tx || tx.pending) return false;
  if ((tx.amountCents || 0) <= 0) return false; // outflows only
  if (!expenseKeywords) return false;
  return categorizeExpense(tx.merchantName, tx.description, expenseKeywords) === 'maintenance';
}

/**
 * Roll up rent + NNN payments by ISO month. Pass the operator's expense-keyword
 * map (expenseKeywords.getKeywords()) to ALSO include utility bills and MRO
 * (maintenance, repair & operations) — the Rent, utilities & MRO report does;
 * pnl.js does not (it adds utilities itself and keeps MRO out of its rent
 * ribbon). Each row is tagged `_bucket: 'rent' | 'utility' | 'mro'`. Returns:
 *   monthly[]: [{ ym, total, rentTotal, utilTotal, mroTotal, rows }] newest-first
 *   thisMonth: the current ISO month's bucket (or zeros)
 *   ytdThis / ytdPrior: calendar-year totals for the current and previous year
 *   ytdThisBy / ytdPriorBy: the same YTD windows split { rent, utility, mro }
 *   total: trailing-window total
 */
export function summarize(txs, keywords, expenseKeywords = null) {
  const months = new Map();
  let total = 0;
  for (const tx of txs || []) {
    // An explicit Spending-tab categorization (_spendCat, stamped by the
    // report loader) is authoritative both ways: rerouted elsewhere → out,
    // even on a keyword match; categorized rent_utilities / maintenance →
    // in, even without one. Stamped maintenance only counts when the caller
    // opted into the expense-keyword buckets (the report path) — pnl.js
    // calls without them and must keep its rent ribbon MRO-free.
    const stampedMro = expenseKeywords != null && tx._spendCat === 'maintenance';
    if (tx._spendCat && tx._spendCat !== 'rent_utilities' && !stampedMro) continue;
    const bucket = stampedMro ? 'mro'
      : isRent(tx, keywords) ? 'rent'
      : isUtilityBill(tx, expenseKeywords) ? 'utility'
      : tx._spendCat === 'rent_utilities' ? 'utility'
      : isMroBill(tx, expenseKeywords) ? 'mro'
      : null;
    if (!bucket) continue;
    const ym = (tx.date || '').slice(0, 7);
    if (!ym) continue;
    if (!months.has(ym)) months.set(ym, { ym, total: 0, rentTotal: 0, utilTotal: 0, mroTotal: 0, rows: [] });
    const m = months.get(ym);
    const cents = tx.amountCents || 0;
    m.total += cents;
    if (bucket === 'utility') m.utilTotal += cents;
    else if (bucket === 'mro') m.mroTotal += cents;
    else m.rentTotal += cents;
    m.rows.push({ ...tx, _bucket: bucket });
    total += cents;
  }
  for (const m of months.values()) m.rows.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  const arr = [...months.values()].sort((a, b) => b.ym.localeCompare(a.ym));
  const today = new Date();
  const thisYear = today.getFullYear();
  const priorYear = thisYear - 1;
  const monthStr = String(today.getMonth() + 1).padStart(2, '0');
  const thisYm = `${thisYear}-${monthStr}`;
  const sumBy = (list) => list.reduce(
    (s, m) => ({ rent: s.rent + m.rentTotal, utility: s.utility + m.utilTotal, mro: s.mro + m.mroTotal }),
    { rent: 0, utility: 0, mro: 0 });
  const thisMonths = arr.filter((m) => m.ym.startsWith(`${thisYear}-`));
  const ytdThis = thisMonths.reduce((s, m) => s + m.total, 0);
  // Compare against the same elapsed window of the previous year (Jan-thru-today)
  // so YoY isn't unfair in the early months of the year.
  const priorMonths = arr.filter((m) => m.ym.startsWith(`${priorYear}-`) && m.ym.slice(5) <= monthStr);
  const ytdPrior = priorMonths.reduce((s, m) => s + m.total, 0);
  const ytdThisBy = sumBy(thisMonths);
  const ytdPriorBy = sumBy(priorMonths);
  const thisMonth = months.get(thisYm) || { ym: thisYm, total: 0, rentTotal: 0, utilTotal: 0, mroTotal: 0, rows: [] };
  return { monthly: arr, thisMonth, thisYm, ytdThis, ytdPrior, ytdThisBy, ytdPriorBy, thisYear, priorYear, total };
}
